import { Link } from "@mui/material";
import Alert from "@mui/material/Alert";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { type QueryTarget } from "@/shared/domain/query";
import {
  useConvertRoboqlToSpecMutation,
  useRoboqlExternalCtx,
} from "@/shared/domain/query/hooks";
import { docsUrl } from "@/shared/environment";
import { OpenInNewIcon } from "@/shared/foundation/icons/mui";
import { RobotoButton } from "@/shared/foundation/primitives";
import type { SearchQueryBody } from "@/types";

import styles from "./RoboqlFilterMenu.module.css";

// Lazy the Monaco editor specifically because it's a heavy dep
const RoboqlEditor = React.lazy(() =>
  import("@/shared/components/RoboqlEditor").then((m) => ({
    default: m.RoboqlEditor,
  })),
);

const ROBOQL_DOCS_URL = docsUrl("/roboql/overview");

export interface RoboqlFilterMenuProps {
  target: QueryTarget;
  /** Seed value for the editor — empty when entering fresh, the active
   *  query text when editing via the chip body. */
  initialValue: string;
  /** Fired after the convert mutation resolves successfully.
   *  Should commit state and close the menu. */
  onExecute: (query: string, spec: SearchQueryBody) => void;
  /** Fired on Cancel — close the menu without changing state. The user
   *  reaches "clear my active RoboQL" by closing the menu and clicking
   *  the chip's `×` (or the chip-row's Clear button). */
  onCancel: () => void;
}

export function RoboqlFilterMenu({
  target,
  initialValue,
  onExecute,
  onCancel,
}: RoboqlFilterMenuProps) {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);

  const externalCtx = useRoboqlExternalCtx(target, resolvedOrgId);
  const convertRoboqlMutation = useConvertRoboqlToSpecMutation();

  const [text, setText] = React.useState(initialValue);

  const handleChange = (value: string | undefined) => {
    setText(value ?? "");
    if (convertRoboqlMutation.isError) {
      convertRoboqlMutation.reset();
    }
  };

  const trimmed = text.trim();
  const executeDisabled =
    trimmed.length === 0 || convertRoboqlMutation.isPending;

  const handleExecute = () => {
    if (executeDisabled) {
      return;
    }
    convertRoboqlMutation.mutate(
      { query: trimmed, orgId: resolvedOrgId },
      {
        onSuccess: (spec) => {
          onExecute(trimmed, spec);
        },
      },
    );
  };

  return (
    <div className={styles.shell}>
      <div className={styles.editorFrame}>
        <RoboqlEditorBoundary>
          <React.Suspense
            fallback={
              <div className={styles.editorFallback}>Loading editor…</div>
            }
          >
            <RoboqlEditor
              target={target}
              roboqlExternalCtx={externalCtx}
              initialValue={initialValue}
              onChange={handleChange}
              onCommit={handleExecute}
              focusEndOnMount
              containerBorder={0}
              containerClassName={styles.editorContainer}
              editorClassName={styles.editor}
              editorOptions={{
                padding: { top: 8 },
                placeholder:
                  'e.g. name CONTAINS "highway" AND created > 2026-04-01',
              }}
            />
          </React.Suspense>
        </RoboqlEditorBoundary>
      </div>
      {convertRoboqlMutation.error && (
        <Alert severity="error" className={styles.errorAlert}>
          {convertRoboqlMutation.error.message}
        </Alert>
      )}
      <div className={styles.footer}>
        <Link
          href={ROBOQL_DOCS_URL}
          target="_blank"
          rel="noreferrer noopener"
          className={styles.docsLink}
        >
          Docs
          <OpenInNewIcon className={styles.docsIcon} aria-hidden="true" />
        </Link>
        <RobotoButton variant="secondary" onClick={onCancel}>
          Cancel
        </RobotoButton>
        <RobotoButton
          variant="primary"
          onClick={handleExecute}
          disabled={executeDisabled}
          loading={convertRoboqlMutation.isPending}
          data-testid="roboql-execute"
        >
          Execute
        </RobotoButton>
      </div>
    </div>
  );
}

/**
 * Isolates Monaco crashes (worker init, web-worker module resolution,
 * theme-init races) to the menu instead of taking down the whole table.
 * The app-level error boundary still catches anything that escapes.
 *
 * Defined inline as a local class boundary — React doesn't ship one and
 * the codebase doesn't yet have a foundation primitive for this. If
 * other surfaces grow to need a similar guard, extract to a shared
 * `<ErrorBoundary>` then.
 */
class RoboqlEditorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.editorFallback}>
          RoboQL editor failed to load — use property filters instead.
        </div>
      );
    }
    return this.props.children;
  }
}
